import { merge } from "lodash";
import { type FormValueEventInstance, type FormValues, RecordSubject } from "./types";

/**
 * The canonical "no proximity" value — used by the outbound mapper to clear
 * proximity in range mode and by the empty-year hygiene strip. This must
 * never carry the experiment default; it represents the absence of proximity.
 */
export const EMPTY_DATE_PROXIMITY: FormValueEventInstance["dateProximity"] = {
    year: null,
    month: null,
    day: null,
};

export const EVENT_DEFAULT_INSTANCE: FormValueEventInstance = {
    date: {
        year: null,
        month: null,
        day: null,
        endYear: null,
    },
    location: "",
    gpid: "",
    dateProximity: EMPTY_DATE_PROXIMITY,
    locationExactness: null,
};

/**
 * Creates a fresh event instance with the configured year proximity default.
 *
 * - In the test arm (`defaultYearProximity = 10`), proximity is `{ year: 10, month: 0, day: 0 }`
 * - In the control arm (`defaultYearProximity = null`), proximity is `{ year: null, month: null, day: null }`
 *
 * Always returns a new object — safe for direct mutation or array assignment.
 */
export const createEventDefaultInstance = (defaultYearProximity: number | null): FormValueEventInstance => ({
    date: {
        year: null,
        month: null,
        day: null,
        endYear: null,
    },
    location: "",
    gpid: "",
    dateProximity: {
        year: defaultYearProximity,
        month: defaultYearProximity !== null ? 0 : null,
        day: defaultYearProximity !== null ? 0 : null,
    },
    locationExactness: null,
});

export const DEFAULT_GIVEN_NAME_EXACTNESS: FormValues["name"]["givenNameExactness"] = {
    flags: {
        phonetic: false,
        similar: false,
        initials: false,
    },
    isExact: false,
};

export const DEFAULT_SURNAME_EXACTNESS: FormValues["name"]["surnameExactness"] = {
    flags: {
        phonetic: false,
        similar: false,
        soundex: false,
    },
    isExact: false,
};

export const RELATION_DEFAULT_INSTANCE: FormValues["name"] = {
    givenName: "",
    surname: "",
    secondSurname: "",
    givenNameExactness: DEFAULT_GIVEN_NAME_EXACTNESS,
    surnameExactness: DEFAULT_SURNAME_EXACTNESS,
};

const getRelationDefaultValue = (withDefaultInstances = false) => ({
    instances: withDefaultInstances ? [RELATION_DEFAULT_INSTANCE] : [],
});

const getEventDefaultValue = (withDefaultInstances = false) => ({
    instances: withDefaultInstances ? [EVENT_DEFAULT_INSTANCE] : [],
    relation: "",
    eventName: "",
});

export const getEmptyFormValues = (
    {
        withDefaultInstances,
        formValuePartials,
    }: {
        withDefaultInstances?: boolean;
        formValuePartials?: Partial<FormValues>;
    } = { withDefaultInstances: false },
): FormValues => {
    let formValues: FormValues = {
        name: {
            givenName: "",
            surname: "",
            secondSurname: "",
            givenNameExactness: {
                flags: {
                    phonetic: false,
                    similar: false,
                    initials: false,
                },
                isExact: false,
            },
            surnameExactness: {
                flags: {
                    phonetic: false,
                    similar: false,
                    soundex: false,
                },
                isExact: false,
            },
        },
        family: {
            father: getRelationDefaultValue(withDefaultInstances),
            mother: getRelationDefaultValue(withDefaultInstances),
            parent: getRelationDefaultValue(withDefaultInstances),
            spouse: getRelationDefaultValue(withDefaultInstances),
            child: getRelationDefaultValue(withDefaultInstances),
            sibling: getRelationDefaultValue(withDefaultInstances),
        },
        events: {
            SelfBirth: getEventDefaultValue(withDefaultInstances),
            SelfMarriage: getEventDefaultValue(withDefaultInstances),
            SelfDeath: getEventDefaultValue(withDefaultInstances),
            SelfResidence: getEventDefaultValue(withDefaultInstances),
            SelfMilitary: getEventDefaultValue(withDefaultInstances),
            SelfDeparture: getEventDefaultValue(withDefaultInstances),
            SelfArrival: getEventDefaultValue(withDefaultInstances),
            SelfDivorce: getEventDefaultValue(withDefaultInstances),
            SelfBaptism: getEventDefaultValue(withDefaultInstances),
            SelfBurial: getEventDefaultValue(withDefaultInstances),
            Self: getEventDefaultValue(withDefaultInstances),
        },
        treePerson: {
            treeId: "",
            personId: "",
        },
        types: {
            trees: true,
            records: true,
            photos: true,
            stories: true,
        },
        priority: "default",
        location: [],
        fields: {},
        recordSubject: RecordSubject.All,
        showUnviewedRecordsOnly: false,
        timespan: {
            yearStart: null,
            yearEnd: null,
            matchMode: "rankOnly",
            isVisible: false,
        },
    };

    if (formValuePartials) {
        formValues = merge(formValues, formValuePartials);
    }

    return formValues;
};
